import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
    AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";
import {
    Lock, Target, TrendingUp, Users, Search, ArrowRight, CheckCircle2,
    Plus, Play, Copy, Trash2, History, Clock, ChevronDown, ChevronRight, Loader2, FileSearch, Download,
    ArrowUp, ArrowDown, ArrowUpDown, X,
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** 1–2 digit numeric tokens → sic_divisions; longer → sic_codes */
function parseSicInput(raw) {
    if (!raw.trim()) return { sic_divisions: null, sic_codes: null };
    const tokens = raw.split(/[\s,;]+/).map(t => t.trim()).filter(Boolean);
    const divisions = [], codes = [];
    for (const t of tokens) {
        if (/^\d{1,2}$/.test(t)) divisions.push(parseInt(t, 10));
        else if (t.length > 0) codes.push(t);
    }
    return {
        sic_divisions: divisions.length ? divisions : null,
        sic_codes: codes.length ? codes : null,
    };
}

function parseGeo(raw) {
    if (!raw.trim()) return null;
    const parts = raw.split(/[,;]/).map(g => g.trim()).filter(Boolean);
    return parts.length ? parts : null;
}

function fmtTV(n) {
    if (n == null) return null;
    if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}m`;
    if (n >= 1_000) return `£${Math.round(n / 1_000)}k`;
    return `£${Number(n).toLocaleString("en-GB")}`;
}

function criteriaBlurb(c) {
    if (!c) return "No criteria";
    const parts = [];
    const hasSic = (c.sic_divisions?.length || 0) + (c.sic_codes?.length || 0) > 0;
    if (hasSic) {
        const items = [...(c.sic_divisions || []).map(String), ...(c.sic_codes || [])];
        parts.push(`SIC ${items.slice(0, 3).join(", ")}${items.length > 3 ? ` +${items.length - 3}` : ""}`);
    }
    if (c.geography?.length) {
        const g = c.geography;
        parts.push(`${g.slice(0, 2).join(", ")}${g.length > 2 ? ` +${g.length - 2}` : ""}`);
    }
    if (c.age_min_years != null || c.age_max_years != null) {
        const mn = c.age_min_years, mx = c.age_max_years;
        if (mn != null && mx != null) parts.push(`${mn}–${mx} yr`);
        else if (mn != null) parts.push(`≥${mn} yr`);
        else parts.push(`≤${mx} yr`);
    }
    if (c.turnover_min != null || c.turnover_max != null) {
        const mn = fmtTV(c.turnover_min), mx = fmtTV(c.turnover_max);
        if (mn && mx) parts.push(`TV ${mn}–${mx}`);
        else if (mn) parts.push(`TV ≥${mn}`);
        else parts.push(`TV ≤${mx}`);
    }
    return parts.length ? parts.join(" · ") : "No criteria filters";
}

function scoreBadgeClass(score, maxScore) {
    if (!maxScore) return "bg-gray-100 text-gray-500 border-gray-200";
    const r = score / maxScore;
    if (r >= 0.75) return "bg-emerald-100 text-emerald-700 border-emerald-200";
    if (r >= 0.4) return "bg-amber-100 text-amber-700 border-amber-200";
    return "bg-red-50 text-red-600 border-red-200";
}

function getReasonStyle(reason) {
    if (!reason) return { cls: "text-[#94A3B8]", prefix: "—" };
    if (reason.includes("not disclosed")) return { cls: "text-[#94A3B8]", prefix: "—" };
    if (reason.includes("outside range")) return { cls: "text-amber-600", prefix: "✗" };
    return { cls: "text-[#15803D]", prefix: "✓" };
}

// ─── Upsell / Locked view ─────────────────────────────────────────────────────

const FEATURES = [
    { icon: Target, text: "Proactive M&A target identification across your mandate criteria" },
    { icon: Search, text: "AI-driven sector screening from public data and proprietary signals" },
    { icon: Users, text: "Warm introduction tracking through your relationship network" },
    { icon: TrendingUp, text: "Mandate-to-target matching with readiness and fit scoring" },
];

export function SourcingLocked() {
    return (
        <div className="min-h-[calc(100vh-44px)] flex items-center justify-center p-8 bg-[#F8FAFC]"
             data-testid="sourcing-locked">
            <div className="max-w-lg w-full">
                <div className="mb-6 flex items-center gap-3">
                    <div
                        className="w-10 h-10 flex items-center justify-center rounded-[6px]"
                        style={{ background: 'rgba(201,162,39,0.12)', border: '1px solid rgba(201,162,39,0.3)' }}
                    >
                        <Lock className="w-5 h-5" style={{ color: '#C9A227' }} strokeWidth={1.75} />
                    </div>
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: '#C9A227' }}>
                            Add-on Surface
                        </p>
                        <h1 className="text-xl font-bold tracking-tight text-[#0B1F3A]">Closvio Sourcing</h1>
                    </div>
                </div>
                <p className="text-sm text-[#475569] leading-relaxed mb-6">
                    Sourcing moves deal origination from reactive inbound to proactive outreach —
                    matching your live mandates against a continuously enriched candidate universe.
                </p>
                <div className="space-y-3 mb-8">
                    {FEATURES.map(({ icon: Icon, text }, i) => (
                        <div key={i} className="flex items-start gap-3">
                            <div
                                className="mt-0.5 w-6 h-6 flex items-center justify-center rounded-[4px] flex-shrink-0"
                                style={{ background: 'rgba(11,31,58,0.06)' }}
                            >
                                <Icon className="w-3.5 h-3.5 text-[#0B1F3A]" strokeWidth={1.75} />
                            </div>
                            <p className="text-sm text-[#334155]">{text}</p>
                        </div>
                    ))}
                </div>
                <div className="border border-[#E2E8F0] rounded-[6px] bg-white p-5 flex items-center justify-between gap-4">
                    <div>
                        <p className="text-sm font-medium text-[#0B1F3A]">Enable Closvio Sourcing</p>
                        <p className="text-xs text-[#64748B] mt-0.5">Contact your account manager to activate this surface.</p>
                    </div>
                    <Button
                        size="sm"
                        className="flex-shrink-0 gap-1.5"
                        style={{ background: '#0B1F3A', color: '#FFFFFF' }}
                        data-testid="sourcing-contact-sales-btn"
                        onClick={() => window.open("mailto:sales@closvio.com?subject=Closvio Sourcing — Enable Request", "_blank")}
                    >
                        Contact Sales <ArrowRight className="w-3.5 h-3.5" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyMandates({ onCreate }) {
    return (
        <div
            data-testid="sourcing-empty-state"
            className="border border-dashed border-[#CBD5E1] rounded-[6px] bg-white p-12 text-center"
        >
            <div
                className="w-12 h-12 flex items-center justify-center rounded-[8px] mx-auto mb-4"
                style={{ background: 'rgba(201,162,39,0.10)', border: '1px solid rgba(201,162,39,0.25)' }}
            >
                <Target className="w-6 h-6" style={{ color: '#C9A227' }} />
            </div>
            <h3 className="text-[15px] font-semibold text-[#0B1F3A] mb-1.5">No mandates yet</h3>
            <p className="text-sm text-[#64748B] mb-6 max-w-sm mx-auto">
                Create your first sourcing mandate to match target companies against your acquisition criteria.
            </p>
            <Button
                data-testid="create-first-mandate-btn"
                onClick={onCreate}
                style={{ background: '#0B1F3A', color: '#FFFFFF' }}
            >
                <Plus className="w-4 h-4 mr-1.5" /> Create your first mandate
            </Button>
        </div>
    );
}

// ─── Create mandate dialog ────────────────────────────────────────────────────

const BLANK_FORM = {
    label: "", sic_raw: "", geography_raw: "",
    age_min: "", age_max: "", turnover_min: "", turnover_max: "",
    exclude_pipeline: true,
};

function CreateMandateDialog({ open, onOpenChange, onSaved, mandate = null }) {
    const isEdit = !!mandate;
    const [form, setForm] = useState(BLANK_FORM);
    const [saving, setSaving] = useState(false);

    // Pre-fill form when dialog opens (create → blank; edit → mandate values)
    useEffect(() => {
        if (!open) return;
        if (!mandate) { setForm(BLANK_FORM); return; }
        const c = mandate.criteria || {};
        const sicParts = [...(c.sic_divisions || []).map(String), ...(c.sic_codes || [])];
        setForm({
            label: mandate.label || "",
            sic_raw: sicParts.join(", "),
            geography_raw: (c.geography || []).join(", "),
            age_min: c.age_min_years != null ? String(c.age_min_years) : "",
            age_max: c.age_max_years != null ? String(c.age_max_years) : "",
            turnover_min: c.turnover_min != null ? String(c.turnover_min) : "",
            turnover_max: c.turnover_max != null ? String(c.turnover_max) : "",
            exclude_pipeline: c.exclude_if_in_pipeline ?? true,
        });
    }, [open, mandate?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.label.trim()) { toast.error("Label is required"); return; }
        const { sic_divisions, sic_codes } = parseSicInput(form.sic_raw);
        const geography = parseGeo(form.geography_raw);
        const criteria = {
            sic_divisions,
            sic_codes,
            geography,
            age_min_years: form.age_min !== "" ? parseInt(form.age_min, 10) : null,
            age_max_years: form.age_max !== "" ? parseInt(form.age_max, 10) : null,
            turnover_min: form.turnover_min !== "" ? parseFloat(form.turnover_min) : null,
            turnover_max: form.turnover_max !== "" ? parseFloat(form.turnover_max) : null,
            exclude_if_in_pipeline: form.exclude_pipeline,
        };
        setSaving(true);
        try {
            let data;
            if (isEdit) {
                ({ data } = await api.patch(`/sourcing/mandates/${mandate.id}`, { label: form.label.trim(), criteria }));
                toast.success("Mandate updated");
            } else {
                ({ data } = await api.post("/sourcing/mandates", { label: form.label.trim(), criteria }));
                toast.success("Mandate created");
                setForm(BLANK_FORM);
            }
            onSaved(data);
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || (isEdit ? "Failed to update mandate" : "Failed to create mandate"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid={isEdit ? "edit-mandate-dialog" : "create-mandate-dialog"}>
                <DialogHeader>
                    <DialogTitle>{isEdit ? "Edit Mandate" : "New Sourcing Mandate"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} data-testid="create-mandate-form" className="space-y-4 mt-1">
                    <div>
                        <Label className="text-[13px]">Label <span className="text-red-500">*</span></Label>
                        <Input
                            data-testid="mandate-label-input"
                            value={form.label}
                            onChange={e => set("label", e.target.value)}
                            placeholder="e.g. UK IT Services £1m–£5m turnover"
                            className="mt-1 text-[13px]"
                            required
                        />
                    </div>
                    <div>
                        <Label className="text-[13px]">SIC Codes / Divisions</Label>
                        <Input
                            data-testid="mandate-sic-input"
                            value={form.sic_raw}
                            onChange={e => set("sic_raw", e.target.value)}
                            placeholder="e.g. 62, 63 or 62020 — separate with commas"
                            className="mt-1 text-[13px]"
                        />
                        <p className="text-[11px] text-[#94A3B8] mt-1">
                            1–2 digit numbers treated as SIC divisions; longer values as specific codes.
                        </p>
                    </div>
                    <div>
                        <Label className="text-[13px]">Geography</Label>
                        <Input
                            data-testid="mandate-geography-input"
                            value={form.geography_raw}
                            onChange={e => set("geography_raw", e.target.value)}
                            placeholder="e.g. Surrey, Kent, Greater London"
                            className="mt-1 text-[13px]"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[13px]">Min age (years)</Label>
                            <Input
                                data-testid="mandate-age-min-input"
                                type="number"
                                min="0"
                                value={form.age_min}
                                onChange={e => set("age_min", e.target.value)}
                                placeholder="e.g. 5"
                                className="mt-1 text-[13px]"
                            />
                        </div>
                        <div>
                            <Label className="text-[13px]">Max age (years)</Label>
                            <Input
                                data-testid="mandate-age-max-input"
                                type="number"
                                min="0"
                                value={form.age_max}
                                onChange={e => set("age_max", e.target.value)}
                                placeholder="e.g. 30"
                                className="mt-1 text-[13px]"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[13px]">Turnover min (£)</Label>
                            <Input
                                data-testid="mandate-turnover-min-input"
                                type="number"
                                min="0"
                                value={form.turnover_min}
                                onChange={e => set("turnover_min", e.target.value)}
                                placeholder="e.g. 1000000"
                                className="mt-1 text-[13px]"
                            />
                        </div>
                        <div>
                            <Label className="text-[13px]">Turnover max (£)</Label>
                            <Input
                                data-testid="mandate-turnover-max-input"
                                type="number"
                                min="0"
                                value={form.turnover_max}
                                onChange={e => set("turnover_max", e.target.value)}
                                placeholder="e.g. 10000000"
                                className="mt-1 text-[13px]"
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-between py-2.5 px-3 rounded-[6px] bg-[#F8FAFC] border border-[#E2E8F0]">
                        <div>
                            <p className="text-[13px] font-medium text-[#0B1F3A]">Exclude companies in pipeline</p>
                            <p className="text-[11px] text-[#94A3B8] mt-0.5">Hide candidates already added to your pipeline</p>
                        </div>
                        <Switch
                            data-testid="mandate-exclude-pipeline-toggle"
                            checked={form.exclude_pipeline}
                            onCheckedChange={v => set("exclude_pipeline", v)}
                        />
                    </div>
                    <DialogFooter className="pt-1">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => onOpenChange(false)}
                            data-testid="mandate-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            size="sm"
                            data-testid="mandate-save-btn"
                            disabled={saving}
                            style={{ background: '#0B1F3A', color: '#FFFFFF' }}
                        >
                            {saving
                                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</>
                                : isEdit ? "Save Changes" : "Save Mandate"
                            }
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ─── Delta badge ─────────────────────────────────────────────────────────────

function DeltaBadge({ delta }) {
    if (delta === null) return <span className="text-[#CBD5E1] text-[11px] font-mono">—</span>;
    if (delta === 0) return <span className="text-[#94A3B8] text-[11px] font-mono">0</span>;
    if (delta > 0) return <span className="text-emerald-600 text-[11px] font-mono font-medium">+{delta}</span>;
    return <span className="text-red-500 text-[11px] font-mono font-medium">{delta}</span>;
}

// ─── Run history dialog ───────────────────────────────────────────────────────

function MandateRunHistoryDialog({ mandate, open, onOpenChange }) {
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !mandate) return;
        setRuns([]);
        setLoading(true);
        api.get(`/sourcing/mandates/${mandate.id}/runs`)
            .then(r => setRuns(r.data.runs || []))
            .catch(() => setRuns([]))
            .finally(() => setLoading(false));
    }, [open, mandate?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Runs are newest-first; delta[i] = runs[i].matched - runs[i+1].matched
    const runsWithDelta = runs.map((r, i) => ({
        ...r,
        delta: i < runs.length - 1 ? r.total_matched - runs[i + 1].total_matched : null,
    }));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="run-history-dialog">
                <DialogHeader>
                    <DialogTitle>Run History</DialogTitle>
                    <DialogDescription className="truncate" title={mandate?.label}>
                        {mandate?.label}
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="py-10 flex justify-center" data-testid="run-history-loading">
                        <Loader2 className="w-4 h-4 animate-spin text-[#94A3B8]" />
                    </div>
                ) : runsWithDelta.length === 0 ? (
                    <div className="py-12 text-center" data-testid="run-history-empty">
                        <Clock className="w-7 h-7 text-[#CBD5E1] mx-auto mb-2.5" strokeWidth={1.5} />
                        <p className="text-sm text-[#94A3B8]">No runs yet</p>
                        <p className="text-xs text-[#CBD5E1] mt-1">Run this mandate to start building history.</p>
                    </div>
                ) : (
                    <div data-testid="run-history-list" className="-mx-6">
                        <table className="w-full text-[13px]">
                            <thead className="border-b border-[#F1F5F9]">
                                <tr>
                                    <th className="px-6 py-2 text-left text-[11px] font-medium text-[#94A3B8] uppercase tracking-wider">Date / Time</th>
                                    <th className="px-4 py-2 text-right text-[11px] font-medium text-[#94A3B8] uppercase tracking-wider">Matched</th>
                                    <th className="px-4 py-2 text-right text-[11px] font-medium text-[#94A3B8] uppercase tracking-wider">vs. prev</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runsWithDelta.map(r => (
                                    <tr
                                        key={r.id}
                                        data-testid={`history-run-${r.id}`}
                                        className="border-t border-[#F8FAFC] hover:bg-[#F8FAFC]"
                                    >
                                        <td className="px-6 py-2.5 text-[#475569]">
                                            {new Date(r.run_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono font-semibold text-[#0B1F3A]">
                                            {r.total_matched}
                                        </td>
                                        <td className="px-4 py-2.5 text-right">
                                            <DeltaBadge delta={r.delta} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {runs.length === 20 && (
                            <p className="text-[11px] text-[#CBD5E1] text-center py-3 px-6">
                                Showing most recent 20 runs
                            </p>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

// ─── Mandate card ─────────────────────────────────────────────────────────────

function MandateCard({ mandate, isActive, running, duplicating, onRun, onDuplicate, onDelete, onHistory }) {
    const blurb = criteriaBlurb(mandate.criteria);
    return (
        <div
            data-testid={`mandate-card-${mandate.id}`}
            className={`border rounded-[6px] bg-white px-5 py-4 flex items-start justify-between gap-4 transition-colors ${
                isActive
                    ? "border-[#0B1F3A] shadow-sm"
                    : "border-[#E2E8F0] hover:border-[#CBD5E1]"
            }`}
        >
            <div className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold text-[#0B1F3A] truncate" data-testid={`mandate-label-${mandate.id}`}>
                    {mandate.label}
                </p>
                <p className="text-[12px] text-[#64748B] mt-0.5 truncate">{blurb}</p>
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#94A3B8]">
                    <span>Created {new Date(mandate.created_at).toLocaleDateString("en-GB")}</span>
                    {mandate.last_run_at && (
                        <>
                            <span className="text-[#CBD5E1]">·</span>
                            <span data-testid={`mandate-last-run-${mandate.id}`}>
                                Last run {new Date(mandate.last_run_at).toLocaleDateString("en-GB")}
                            </span>
                        </>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`mandate-history-btn-${mandate.id}`}
                    onClick={() => onHistory(mandate)}
                    className="h-8 w-8 p-0 text-[#CBD5E1] hover:text-[#0B1F3A] hover:bg-[#F1F5F9]"
                    title="View run history"
                >
                    <History className="w-3.5 h-3.5" />
                </Button>
                <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`mandate-delete-btn-${mandate.id}`}
                    disabled={running || duplicating}
                    onClick={() => onDelete(mandate)}
                    className="h-8 w-8 p-0 text-[#CBD5E1] hover:text-red-500 hover:bg-red-50"
                    title="Delete mandate"
                >
                    <Trash2 className="w-3.5 h-3.5" />
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    data-testid={`mandate-duplicate-btn-${mandate.id}`}
                    disabled={duplicating || running}
                    onClick={() => onDuplicate(mandate)}
                    className="gap-1.5 text-[13px]"
                >
                    {duplicating
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Copy className="w-3 h-3" />
                    }
                    {duplicating ? "…" : "Duplicate"}
                </Button>
                <Button
                    size="sm"
                    data-testid={`mandate-run-btn-${mandate.id}`}
                    disabled={running}
                    onClick={() => onRun(mandate)}
                    className="flex-shrink-0 gap-1.5 text-[13px]"
                    style={{ background: '#0B1F3A', color: '#FFFFFF' }}
                >
                    {running
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Running…</>
                        : <><Play className="w-3 h-3" /> Run</>
                    }
                </Button>
            </div>
        </div>
    );
}

// ─── CSV helpers (mandate results export) ────────────────────────────────────

function csvEscape(val) {
    const s = (val == null || val === "—") ? "" : String(val);
    return `"${s.replace(/"/g, '""')}"`;
}

function resultSicLabel(r) {
    if (r.sic_divisions?.length) return `SIC ${r.sic_divisions.slice(0, 2).join(", ")}`;
    if (r.sic_codes?.length) return r.sic_codes[0];
    return "";
}

// ─── Results panel ────────────────────────────────────────────────────────────

function ResultsPanel({ mandate, runResult, onAddToPipeline, addingPipeline, selectedNumbers, onToggleSelect, onSetSelected, onClearSelection, onBulkAdd, bulkAdding }) {
    const [expanded, setExpanded] = useState({});
    const [sortCol, setSortCol] = useState(null);       // null | "company_name" | "score" | "in_pipeline"
    const [sortDir, setSortDir] = useState(null);       // null | "asc" | "desc"
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [pipelineFilter, setPipelineFilter] = useState(null); // null | "not_in_pipeline" | "in_pipeline"
    const [scoreFilter, setScoreFilter] = useState(null);       // null | "high_match"
    const [successionOnly, setSuccessionOnly] = useState(false); // owner-age succession signal
    const results = runResult.results || [];

    const toggle = (num) => setExpanded(prev => ({ ...prev, [num]: !prev[num] }));

    // Reset sort, search, and both filter groups when a new run is loaded
    useEffect(() => {
        setSortCol(null);
        setSortDir(null);
        setSearchText("");
        setDebouncedSearch("");
        setPipelineFilter(null);
        setScoreFilter(null);
        setSuccessionOnly(false);
    }, [runResult?.run_at]);

    // Debounce search input — 250ms
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchText), 250);
        return () => clearTimeout(t);
    }, [searchText]);

    const handleClearSearch = () => {
        setSearchText("");
        setDebouncedSearch(""); // immediate, no delay
    };

    const handleStatusFilterSelect = (key) => {
        setPipelineFilter(key);
        onClearSelection();
    };

    const handleScoreFilterSelect = (key) => {
        setScoreFilter(key);
        onClearSelection();
    };

    // Three-state sort: first click = firstDir, second = opposite, third = default
    const handleSort = (col, firstDir = "asc") => {
        const secondDir = firstDir === "asc" ? "desc" : "asc";
        if (sortCol !== col) {
            setSortCol(col);
            setSortDir(firstDir);
        } else if (sortDir === firstDir) {
            setSortDir(secondDir);
        } else {
            setSortCol(null);
            setSortDir(null);
        }
    };

    // Counts from raw results — per-dimension, independent of the other group's selection
    const filterCounts = {
        not_in_pipeline: results.filter(r => !r.in_pipeline).length,
        in_pipeline: results.filter(r => r.in_pipeline).length,
        high_match: results.filter(r => r.max_score && r.score / r.max_score >= 0.70).length,
        succession: results.filter(r => r.psc_summary?.succession_flag).length,
    };

    // Step 1 — status filter (pipeline-status group)
    const statusFiltered = pipelineFilter === "not_in_pipeline"
        ? results.filter(r => !r.in_pipeline)
        : pipelineFilter === "in_pipeline"
        ? results.filter(r => r.in_pipeline)
        : results;

    // Step 2 — score filter (score-band group, AND with status)
    const scoreFiltered = scoreFilter === "high_match"
        ? statusFiltered.filter(r => r.max_score && r.score / r.max_score >= 0.70)
        : statusFiltered;

    // Step 2b — owner succession filter (oldest active individual PSC at/above succession age)
    const ownerFiltered = successionOnly
        ? scoreFiltered.filter(r => r.psc_summary?.succession_flag)
        : scoreFiltered;

    // Step 3 — search filter (on top of the filter groups)
    const searchFiltered = debouncedSearch.trim()
        ? ownerFiltered.filter(r => (r.company_name || "").toLowerCase().includes(debouncedSearch.trim().toLowerCase()))
        : ownerFiltered;

    // Step 3 — sort
    const sortedResults = (() => {
        if (!sortCol || !sortDir) return searchFiltered;
        return [...searchFiltered].sort((a, b) => {
            let aVal, bVal;
            if (sortCol === "company_name") {
                aVal = (a.company_name || "").toLowerCase();
                bVal = (b.company_name || "").toLowerCase();
            } else if (sortCol === "score") {
                aVal = a.score ?? 0;
                bVal = b.score ?? 0;
            } else if (sortCol === "owner_age") {
                aVal = a.psc_summary?.oldest_age ?? -1;
                bVal = b.psc_summary?.oldest_age ?? -1;
            } else { // in_pipeline
                aVal = a.in_pipeline ? 1 : 0;
                bVal = b.in_pipeline ? 1 : 0;
            }
            if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
            if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
    })();

    const selectableNumbers = sortedResults.filter(r => !r.in_pipeline).map(r => r.company_number);
    const allSelected = selectableNumbers.length > 0 && selectableNumbers.every(n => selectedNumbers.has(n));
    const someSelected = selectableNumbers.some(n => selectedNumbers.has(n)) && !allSelected;
    const headerChecked = allSelected ? true : someSelected ? "indeterminate" : false;
    const selectedCount = [...selectedNumbers].filter(n => sortedResults.some(r => r.company_number === n)).length;

    const handleHeaderCheck = () => {
        onSetSelected(allSelected ? new Set() : new Set(selectableNumbers));
    };

    const handleExportCsv = () => {
        const header = ["Company Name", "Company Number", "SIC Code", "Location", "Match Score", "Match Reasons", "Already in Pipeline", "Oldest PSC Age", "Succession Signal"];
        const csvRows = sortedResults.map(r => [
            csvEscape(r.company_name),
            csvEscape(r.company_number),
            csvEscape(resultSicLabel(r)),
            csvEscape(r.registered_county || r.registered_country),
            csvEscape(`${r.score}/${r.max_score}`),
            csvEscape((r.reasons || []).join("; ")),
            csvEscape(r.in_pipeline ? "Yes" : "No"),
            csvEscape(r.psc_summary?.oldest_age ?? ""),
            csvEscape(r.psc_summary?.succession_flag ? "Yes" : "No"),
        ]);
        const csv = [header.map(csvEscape), ...csvRows].map(row => row.join(",")).join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const dateStr = new Date().toISOString().slice(0, 10);
        const slug = mandate.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 50);
        a.href = url;
        a.download = `closvio-mandate-${slug}-results-${dateStr}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="sourcing-results-panel" className="border border-[#E2E8F0] rounded-[6px] bg-white overflow-hidden">
            {/* Header */}
            <div className="px-5 py-3.5 border-b border-[#E2E8F0] flex items-center justify-between gap-4 bg-[#F8FAFC]">
                <div className="min-w-0">
                    <h2 className="text-[14px] font-semibold text-[#0B1F3A]">
                        Results —{" "}
                        <span className="font-normal text-[#475569]">{mandate.label}</span>
                    </h2>
                    <p className="text-[11px] text-[#94A3B8] mt-0.5">
                        {runResult.total_matched} matched · {runResult.total_evaluated} evaluated ·
                        run {new Date(runResult.run_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                </div>
                <div className="flex items-center gap-2.5 flex-shrink-0">
                    {/* Search input */}
                    <div className="relative" data-testid="results-search-wrapper">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-[#94A3B8] pointer-events-none" />
                        <input
                            data-testid="results-search-input"
                            type="text"
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            placeholder="Search companies…"
                            className="h-7 pl-7 pr-6 text-[12px] w-44 rounded-md border border-[#E2E8F0] bg-white text-[#0B1F3A] placeholder:text-[#CBD5E1] focus:outline-none focus:ring-1 focus:ring-[#0B1F3A] focus:border-[#0B1F3A]"
                        />
                        {searchText && (
                            <button
                                data-testid="results-search-clear"
                                onClick={handleClearSearch}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0B1F3A]"
                                aria-label="Clear search"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        )}
                    </div>
                    <Badge
                        data-testid="results-match-count"
                        className="font-mono text-[11px] bg-[#0B1F3A] text-white border-0"
                    >
                        {runResult.total_matched} companies
                    </Badge>
                    <Button
                        size="sm"
                        variant="outline"
                        data-testid="mandate-export-csv-button"
                        disabled={sortedResults.length === 0}
                        onClick={handleExportCsv}
                        className="h-7 text-[11px] px-2.5 gap-1.5 border-[#E2E8F0] text-[#475569] hover:text-[#0B1F3A] hover:border-[#0B1F3A] disabled:opacity-40"
                    >
                        <Download className="w-3 h-3" />
                        Export CSV
                    </Button>
                </div>
            </div>

            {/* Filter chips — only shown when there are results */}
            {results.length > 0 && (
                <div className="px-5 py-2.5 border-b border-[#E2E8F0] flex items-center gap-x-3 gap-y-2 flex-wrap bg-white" data-testid="results-filter-bar">
                    {/* Status group */}
                    <div className="flex items-center gap-1.5" data-testid="results-filter-group-status">
                        <span className="text-[10px] font-medium text-[#94A3B8] uppercase tracking-wider">Status</span>
                        {[
                            {
                                key: null,
                                label: "All",
                                count: results.length,
                                activeCls: "bg-[#0B1F3A] text-white border-[#0B1F3A]",
                                inactiveCls: "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#0B1F3A] hover:text-[#0B1F3A]",
                            },
                            {
                                key: "not_in_pipeline",
                                label: "Not in Pipeline",
                                count: filterCounts.not_in_pipeline,
                                activeCls: "bg-emerald-50 text-emerald-700 border-emerald-200",
                                inactiveCls: "bg-white text-[#64748B] border-[#E2E8F0] hover:border-emerald-200 hover:text-emerald-700",
                            },
                            {
                                key: "in_pipeline",
                                label: "Already in Pipeline",
                                count: filterCounts.in_pipeline,
                                activeCls: "bg-blue-50 text-blue-700 border-blue-200",
                                inactiveCls: "bg-white text-[#64748B] border-[#E2E8F0] hover:border-blue-200 hover:text-blue-700",
                            },
                        ].map(chip => {
                            const isActive = pipelineFilter === chip.key;
                            return (
                                <button
                                    key={chip.key ?? "status-all"}
                                    data-testid={chip.key ? `results-filter-chip-${chip.key}` : "results-filter-chip-status-all"}
                                    onClick={() => handleStatusFilterSelect(isActive && chip.key !== null ? null : chip.key)}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-medium border transition-all ${isActive ? chip.activeCls : chip.inactiveCls}`}
                                >
                                    {chip.label}
                                    <span className={`font-mono text-[10px] ${isActive ? "opacity-75" : "opacity-55"}`}>
                                        {chip.count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Group divider */}
                    <div className="w-px h-5 bg-[#E2E8F0] self-center" />

                    {/* Score group */}
                    <div className="flex items-center gap-1.5" data-testid="results-filter-group-score">
                        <span className="text-[10px] font-medium text-[#94A3B8] uppercase tracking-wider">Match</span>
                        {[
                            {
                                key: null,
                                label: "All Scores",
                                count: results.length,
                                activeCls: "bg-[#0B1F3A] text-white border-[#0B1F3A]",
                                inactiveCls: "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#0B1F3A] hover:text-[#0B1F3A]",
                            },
                            {
                                key: "high_match",
                                label: "High Match",
                                count: filterCounts.high_match,
                                activeCls: "bg-amber-50 text-amber-700 border-amber-200",
                                inactiveCls: "bg-white text-[#64748B] border-[#E2E8F0] hover:border-amber-200 hover:text-amber-700",
                            },
                        ].map(chip => {
                            const isActive = scoreFilter === chip.key;
                            return (
                                <button
                                    key={chip.key ?? "score-all"}
                                    data-testid={chip.key ? `results-filter-chip-${chip.key}` : "results-filter-chip-score-all"}
                                    onClick={() => handleScoreFilterSelect(isActive && chip.key !== null ? null : chip.key)}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-medium border transition-all ${isActive ? chip.activeCls : chip.inactiveCls}`}
                                >
                                    {chip.label}
                                    <span className={`font-mono text-[10px] ${isActive ? "opacity-75" : "opacity-55"}`}>
                                        {chip.count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="w-px h-5 bg-[#E2E8F0] self-center" />

                    {/* Owner group */}
                    <div className="flex items-center gap-1.5" data-testid="results-filter-group-owner">
                        <span className="text-[10px] font-medium text-[#94A3B8] uppercase tracking-wider">Owner</span>
                        <button
                            data-testid="results-filter-chip-succession"
                            onClick={() => { setSuccessionOnly(v => !v); onClearSelection(); }}
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-medium border transition-all ${successionOnly ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-emerald-200 hover:text-emerald-700"}`}
                        >
                            Succession signal
                            <span className={`font-mono text-[10px] ${successionOnly ? "opacity-75" : "opacity-55"}`}>{filterCounts.succession}</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Bulk action bar — visible when ≥1 row selected */}
            {selectedCount > 0 && (
                <div
                    className="px-5 py-2.5 border-b border-[#E2E8F0] bg-[#EFF6FF] flex items-center justify-between gap-3"
                    data-testid="bulk-action-bar"
                >
                    <span className="text-[12px] font-medium text-blue-700" data-testid="bulk-selected-count">
                        {selectedCount} selected
                    </span>
                    <div className="flex items-center gap-2.5">
                        <button
                            onClick={onClearSelection}
                            data-testid="bulk-clear-btn"
                            className="text-[12px] text-[#64748B] hover:text-[#0B1F3A] underline"
                        >
                            Clear
                        </button>
                        <Button
                            size="sm"
                            data-testid="bulk-add-pipeline-btn"
                            disabled={bulkAdding}
                            onClick={onBulkAdd}
                            className="h-7 text-[12px] px-3 gap-1.5"
                            style={{ background: '#0B1F3A', color: '#FFFFFF' }}
                        >
                            {bulkAdding
                                ? <><Loader2 className="w-3 h-3 animate-spin" /> Adding…</>
                                : <><Plus className="w-3 h-3" /> Add to Pipeline</>
                            }
                        </Button>
                    </div>
                </div>
            )}

            {results.length === 0 ? (
                <div className="py-14 text-center" data-testid="results-empty">
                    <FileSearch className="w-8 h-8 text-[#CBD5E1] mx-auto mb-2.5" strokeWidth={1.5} />
                    <p className="text-sm text-[#94A3B8]">No companies matched these criteria</p>
                    <p className="text-xs text-[#CBD5E1] mt-1">Try broadening the SIC divisions, geography, or turnover range.</p>
                </div>
            ) : sortedResults.length === 0 ? (
                <div className="py-14 text-center" data-testid="results-search-empty">
                    <Search className="w-8 h-8 text-[#CBD5E1] mx-auto mb-2.5" strokeWidth={1.5} />
                    <p className="text-sm text-[#94A3B8]">
                        {(pipelineFilter || scoreFilter) && !debouncedSearch.trim()
                            ? "No companies match this filter"
                            : "No companies match your search"}
                    </p>
                    <p className="text-xs text-[#CBD5E1] mt-1">Try a different company name or clear the search.</p>
                </div>
            ) : (
                <table className="w-full text-[13px]">
                    <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                        <tr>
                            <th className="w-10 px-3 py-2.5" onClick={e => e.stopPropagation()}>
                                {selectableNumbers.length > 0 && (
                                    <Checkbox
                                        data-testid="select-all-checkbox"
                                        checked={headerChecked}
                                        onCheckedChange={handleHeaderCheck}
                                        className="border-[#CBD5E1]"
                                    />
                                )}
                            </th>
                            <th className="w-6 px-3 py-2.5" />
                            {/* Sortable: Company Name */}
                            <th
                                data-testid="results-sort-header-company"
                                onClick={() => handleSort("company_name", "asc")}
                                className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider cursor-pointer select-none group text-[#94A3B8] hover:text-[#0B1F3A]"
                            >
                                <span className="inline-flex items-center gap-1">
                                    Company
                                    {sortCol === "company_name" && sortDir === "asc"  && <ArrowUp   className="w-3 h-3 text-[#0B1F3A]" />}
                                    {sortCol === "company_name" && sortDir === "desc" && <ArrowDown className="w-3 h-3 text-[#0B1F3A]" />}
                                    {sortCol !== "company_name" && <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity" />}
                                </span>
                            </th>
                            {/* Static */}
                            <th className="px-4 py-2.5 text-left text-[11px] font-medium text-[#94A3B8] uppercase tracking-wider">Location</th>
                            {/* Sortable: Match Score (desc-first) */}
                            <th
                                data-testid="results-sort-header-score"
                                onClick={() => handleSort("score", "desc")}
                                className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider cursor-pointer select-none group text-[#94A3B8] hover:text-[#0B1F3A]"
                            >
                                <span className="inline-flex items-center gap-1">
                                    Score
                                    {sortCol === "score" && sortDir === "desc" && <ArrowDown className="w-3 h-3 text-[#0B1F3A]" />}
                                    {sortCol === "score" && sortDir === "asc"  && <ArrowUp   className="w-3 h-3 text-[#0B1F3A]" />}
                                    {sortCol !== "score" && <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity" />}
                                </span>
                            </th>
                            {/* Sortable: Owner age (oldest active individual PSC) */}
                            <th
                                data-testid="results-sort-header-owner-age"
                                onClick={() => handleSort("owner_age", "desc")}
                                className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider cursor-pointer select-none group text-[#94A3B8] hover:text-[#0B1F3A]"
                            >
                                <span className="inline-flex items-center gap-1">
                                    Owner age
                                    {sortCol === "owner_age" && sortDir === "desc" && <ArrowDown className="w-3 h-3 text-[#0B1F3A]" />}
                                    {sortCol === "owner_age" && sortDir === "asc"  && <ArrowUp   className="w-3 h-3 text-[#0B1F3A]" />}
                                    {sortCol !== "owner_age" && <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity" />}
                                </span>
                            </th>
                            {/* Sortable: Already in Pipeline */}
                            <th
                                data-testid="results-sort-header-pipeline"
                                onClick={() => handleSort("in_pipeline", "asc")}
                                className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider cursor-pointer select-none group text-[#94A3B8] hover:text-[#0B1F3A]"
                            >
                                <span className="inline-flex items-center gap-1">
                                    Pipeline
                                    {sortCol === "in_pipeline" && sortDir === "asc"  && <ArrowUp   className="w-3 h-3 text-[#0B1F3A]" />}
                                    {sortCol === "in_pipeline" && sortDir === "desc" && <ArrowDown className="w-3 h-3 text-[#0B1F3A]" />}
                                    {sortCol !== "in_pipeline" && <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity" />}
                                </span>
                            </th>
                            <th className="px-4 py-2.5" />
                        </tr>
                    </thead>
                    <tbody>
                        {sortedResults.map(r => (
                            <Fragment key={r.company_number}>
                                <tr
                                    data-testid={`result-row-${r.company_number}`}
                                    className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC] cursor-pointer"
                                    onClick={() => toggle(r.company_number)}
                                >
                                    <td className="w-10 px-3 py-3" onClick={e => e.stopPropagation()}>
                                        {!r.in_pipeline && (
                                            <Checkbox
                                                data-testid={`select-checkbox-${r.company_number}`}
                                                checked={selectedNumbers.has(r.company_number)}
                                                onCheckedChange={() => onToggleSelect(r.company_number)}
                                                className="border-[#CBD5E1]"
                                            />
                                        )}
                                    </td>
                                    <td className="px-3 py-3 w-6">
                                        {expanded[r.company_number]
                                            ? <ChevronDown className="w-3.5 h-3.5 text-[#94A3B8]" />
                                            : <ChevronRight className="w-3.5 h-3.5 text-[#94A3B8]" />
                                        }
                                    </td>
                                    <td className="px-4 py-3">
                                        <Link to={`/sourcing/company/${r.company_number}`} className="font-medium text-[#0B1F3A] block hover:underline" data-testid={`analysis-link-${r.company_number}`} onClick={e => e.stopPropagation()}>{r.company_name || "—"}</Link>
                                        {r.company_number && (
                                            <a
                                                data-testid={`ch-link-${r.company_number}`}
                                                href={`https://find-and-update.company-information.service.gov.uk/company/${r.company_number}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-mono text-[11px] text-[#64748B] hover:text-[#0B1F3A] hover:underline mt-0.5 block"
                                                onClick={e => e.stopPropagation()}
                                            >
                                                {r.company_number}
                                            </a>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-[#64748B]">
                                        {r.registered_county || r.registered_country || "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span
                                            data-testid={`score-badge-${r.company_number}`}
                                            className={`inline-block font-mono text-[11px] px-2 py-0.5 rounded-full font-medium border ${scoreBadgeClass(r.score, r.max_score)}`}
                                        >
                                            {r.score}/{r.max_score}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3" data-testid={`owner-age-${r.company_number}`}>
                                        {r.psc_summary?.oldest_age != null ? (
                                            <span className="inline-flex items-center gap-1.5">
                                                <span className="font-mono text-[12px] text-[#0B1F3A]">~{r.psc_summary.oldest_age}</span>
                                                {r.psc_summary.succession_flag && (
                                                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-medium">Succession</Badge>
                                                )}
                                            </span>
                                        ) : <span className="text-[#CBD5E1]">—</span>}
                                    </td>
                                    <td className="px-4 py-3">
                                        {r.in_pipeline && (
                                            <Badge
                                                data-testid={`in-pipeline-badge-${r.company_number}`}
                                                className="bg-blue-50 text-blue-700 border-blue-200 text-[11px] font-medium"
                                                variant="outline"
                                            >
                                                In pipeline
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {!r.in_pipeline && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                data-testid={`add-pipeline-btn-${r.company_number}`}
                                                disabled={addingPipeline[r.company_number]}
                                                onClick={(e) => { e.stopPropagation(); onAddToPipeline(r); }}
                                                className="text-[12px] gap-1 h-7 px-2.5"
                                            >
                                                {addingPipeline[r.company_number]
                                                    ? <Loader2 className="w-3 h-3 animate-spin" />
                                                    : <Plus className="w-3 h-3" />
                                                }
                                                {addingPipeline[r.company_number] ? "Adding…" : "Add to pipeline"}
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                                {expanded[r.company_number] && (
                                    <tr data-testid={`reasons-row-${r.company_number}`} className="bg-[#F8FAFC]">
                                        <td colSpan={8} className="px-6 pb-3 pt-1">
                                            {r.psc_summary && (
                                                <p className="text-[11px] text-[#475569] mb-1.5" data-testid={`psc-detail-${r.company_number}`}>
                                                    Owners: {r.psc_summary.active_individual_pscs} active individual PSC{r.psc_summary.active_individual_pscs === 1 ? "" : "s"}
                                                    {r.psc_summary.sole_individual ? " (sole owner)" : ""}
                                                    {r.psc_summary.oldest_age != null ? `, oldest ~${r.psc_summary.oldest_age}` : ""}
                                                </p>
                                            )}
                                            <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                                                {(r.reasons || []).map((reason, i) => {
                                                    const { cls, prefix } = getReasonStyle(reason);
                                                    return (
                                                        <span key={i} className={`text-[11px] ${cls}`}>
                                                            {prefix} {reason}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

// ─── Main entitled view ───────────────────────────────────────────────────────

function SourcingMandates() {
    const [mandates, setMandates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [editingMandate, setEditingMandate] = useState(null);
    const [duplicatingId, setDuplicatingId] = useState(null);
    const [pendingDeleteMandate, setPendingDeleteMandate] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [historyMandate, setHistoryMandate] = useState(null);
    const [runningId, setRunningId] = useState(null);
    const [activeMandate, setActiveMandate] = useState(null);
    const [runResult, setRunResult] = useState(null);
    const [addingPipeline, setAddingPipeline] = useState({});
    const [selectedNumbers, setSelectedNumbers] = useState(new Set());
    const [bulkAdding, setBulkAdding] = useState(false);

    useEffect(() => {
        api.get("/sourcing/mandates")
            .then(r => setMandates(r.data.mandates || []))
            .catch(() => setMandates([]))
            .finally(() => setLoading(false));
    }, []);

    // Handles both create (prepend) and edit/duplicate-save (update in place)
    const handleMandateSaved = (saved) => {
        setMandates(prev => {
            const idx = prev.findIndex(m => m.id === saved.id);
            if (idx >= 0) {
                const updated = [...prev];
                updated[idx] = saved;
                return updated;
            }
            return [saved, ...prev];
        });
    };

    const handleDuplicate = async (mandate) => {
        setDuplicatingId(mandate.id);
        try {
            const { data } = await api.post(`/sourcing/mandates/${mandate.id}/duplicate`);
            setMandates(prev => [data, ...prev]);
            setEditingMandate(data); // open edit dialog immediately
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to duplicate mandate");
        } finally {
            setDuplicatingId(null);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!pendingDeleteMandate) return;
        setDeleting(true);
        try {
            await api.delete(`/sourcing/mandates/${pendingDeleteMandate.id}`);
            setMandates(prev => prev.filter(m => m.id !== pendingDeleteMandate.id));
            if (activeMandate?.id === pendingDeleteMandate.id) {
                setActiveMandate(null);
                setRunResult(null);
            }
            toast.success("Mandate deleted");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete mandate");
        } finally {
            setDeleting(false);
            setPendingDeleteMandate(null);
        }
    };

    const handleRun = async (mandate) => {
        setRunningId(mandate.id);
        setActiveMandate(mandate);
        setRunResult(null);
        setSelectedNumbers(new Set());
        try {
            const { data } = await api.post(`/sourcing/mandates/${mandate.id}/run`);
            setRunResult(data);
            setMandates(prev => prev.map(m =>
                m.id === mandate.id ? { ...m, last_run_at: data.run_at } : m
            ));
            toast.success(`${data.total_matched} compan${data.total_matched === 1 ? "y" : "ies"} matched`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Run failed");
            setActiveMandate(null);
        } finally {
            setRunningId(null);
        }
    };

    const handleAddToPipeline = async (company) => {
        setAddingPipeline(prev => ({ ...prev, [company.company_number]: true }));
        try {
            await api.post("/sourcing/candidates", { company_number: company.company_number });
            toast.success(`${company.company_name} added to pipeline`);
            setRunResult(prev => ({
                ...prev,
                results: prev.results.map(r =>
                    r.company_number === company.company_number ? { ...r, in_pipeline: true } : r
                ),
            }));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to add to pipeline");
        } finally {
            setAddingPipeline(prev => ({ ...prev, [company.company_number]: false }));
        }
    };

    const handleBulkAddToPipeline = async () => {
        const numbers = [...selectedNumbers].filter(n =>
            runResult?.results?.some(r => r.company_number === n && !r.in_pipeline)
        );
        if (numbers.length === 0) return;
        setBulkAdding(true);
        try {
            const { data } = await api.post("/sourcing/candidates/bulk", { company_numbers: numbers });
            const { added_count, skipped_count } = data;
            if (added_count > 0 && skipped_count === 0) {
                toast.success(`${added_count} compan${added_count === 1 ? "y" : "ies"} added to pipeline`);
            } else if (added_count > 0) {
                toast.success(`${added_count} added · ${skipped_count} already in pipeline`);
            } else {
                toast.info("All selected companies are already in pipeline");
            }
            const addedNums = new Set(data.results.filter(r => r.created).map(r => r.company_number));
            setRunResult(prev => ({
                ...prev,
                results: prev.results.map(r =>
                    addedNums.has(r.company_number) ? { ...r, in_pipeline: true } : r
                ),
            }));
            setSelectedNumbers(new Set());
        } catch (err) {
            toast.error(err.response?.data?.detail || "Bulk add failed");
        } finally {
            setBulkAdding(false);
        }
    };

    return (
        <div data-testid="sourcing-mandates-page">
            <PageHeader title="Sourcing" subtitle="Mandate-driven target identification.">
                <Button
                    size="sm"
                    data-testid="create-mandate-btn"
                    onClick={() => setCreateOpen(true)}
                    style={{ background: '#0B1F3A', color: '#FFFFFF' }}
                    className="gap-1.5"
                >
                    <Plus className="w-3.5 h-3.5" /> Create Mandate
                </Button>
            </PageHeader>

            <div className="p-6 lg:p-8 space-y-6 bg-[#F8FAFC] min-h-[calc(100vh-44px)]">
                {/* Mandate list */}
                {loading ? (
                    <div className="py-16 flex justify-center" data-testid="sourcing-loading">
                        <Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" />
                    </div>
                ) : mandates.length === 0 ? (
                    <EmptyMandates onCreate={() => setCreateOpen(true)} />
                ) : (
                    <div className="space-y-2.5" data-testid="mandate-list">
                        <div className="flex items-center justify-between mb-1">
                            <p className="text-[11px] uppercase tracking-[0.1em] font-semibold text-[#94A3B8]">
                                Mandates ({mandates.length})
                            </p>
                        </div>
                        {mandates.map(m => (
                            <MandateCard
                                key={m.id}
                                mandate={m}
                                isActive={activeMandate?.id === m.id && !!runResult}
                                running={runningId === m.id}
                                duplicating={duplicatingId === m.id}
                                onRun={handleRun}
                                onDuplicate={handleDuplicate}
                                onDelete={setPendingDeleteMandate}
                                onHistory={setHistoryMandate}
                            />
                        ))}
                    </div>
                )}

                {/* Run results */}
                {runningId && (
                    <div className="flex items-center justify-center gap-2.5 py-10 text-[#64748B]" data-testid="results-loading">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Matching against company universe…</span>
                    </div>
                )}
                {runResult && activeMandate && !runningId && (
                    <ResultsPanel
                        mandate={activeMandate}
                        runResult={runResult}
                        onAddToPipeline={handleAddToPipeline}
                        addingPipeline={addingPipeline}
                        selectedNumbers={selectedNumbers}
                        onToggleSelect={(num) => setSelectedNumbers(prev => {
                            const next = new Set(prev);
                            next.has(num) ? next.delete(num) : next.add(num);
                            return next;
                        })}
                        onSetSelected={setSelectedNumbers}
                        onClearSelection={() => setSelectedNumbers(new Set())}
                        onBulkAdd={handleBulkAddToPipeline}
                        bulkAdding={bulkAdding}
                    />
                )}
            </div>

            {/* Run history */}
            <MandateRunHistoryDialog
                mandate={historyMandate}
                open={!!historyMandate}
                onOpenChange={(v) => { if (!v) setHistoryMandate(null); }}
            />

            <CreateMandateDialog
                open={createOpen || !!editingMandate}
                onOpenChange={(v) => { if (!v) { setCreateOpen(false); setEditingMandate(null); } }}
                onSaved={handleMandateSaved}
                mandate={editingMandate}
            />

            {/* Delete confirmation */}
            <AlertDialog
                open={!!pendingDeleteMandate}
                onOpenChange={(v) => { if (!v) setPendingDeleteMandate(null); }}
            >
                <AlertDialogContent data-testid="delete-mandate-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete mandate?</AlertDialogTitle>
                        <AlertDialogDescription>
                            <strong>"{pendingDeleteMandate?.label}"</strong> will be permanently removed.
                            Pipeline companies added from its results will remain untouched.
                            This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-mandate-cancel-btn" disabled={deleting}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="delete-mandate-confirm-btn"
                            disabled={deleting}
                            onClick={handleDeleteConfirm}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            {deleting ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Deleting…</> : "Delete"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

// ─── Page entry point ─────────────────────────────────────────────────────────

export default function Sourcing() {
    const { hasSurface } = useAuth();
    return hasSurface("sourcing") ? <SourcingMandates /> : <SourcingLocked />;
}
